/**
 * ============================================================
 * LINKED DWR UTILITIES + MODAL
 * ============================================================
 *
 * Shared by:
 * - Schedule
 * - TodoDraft
 *
 * Supported parent records:
 * - schedule_id
 * - todo_draft_id
 *
 * DWR Status:
 * 0 = Pending
 * 1 = Approved
 * 2 = Rejected
 * 3 = Open
 *
 * Visible DWRs:
 * - Pending
 * - Approved
 *
 * Rejected and Open DWRs are excluded from the visible list.
 */

import React from 'react';

import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    Typography,
} from '@mui/material';

import AssignmentIcon from '@mui/icons-material/Assignment';

import dayjs from 'dayjs';


/**
 * ============================================================
 * GET LINKED DWRs
 * ============================================================
 *
 * Fetch DWRs linked to either a Schedule or TodoDraft.
 *
 * Schedule:
 *
 * const dwrs = await getLinkedDwrs({
 *   authAxios,
 *   route: PRIVATE_ROUTE.GET_DWR_AVAILABLE_WORK,
 *   schedule_id: schedule._id,
 * });
 *
 * TodoDraft:
 *
 * const dwrs = await getLinkedDwrs({
 *   authAxios,
 *   route: PRIVATE_ROUTE.GET_DWR_AVAILABLE_WORK,
 *   todo_draft_id: todoDraft._id,
 * });
 *
 * Returns:
 *
 * [
 *   {
 *     ...
 *   }
 * ]
 */
/**
 * ============================================================
 * GET LINKED DWRs
 * ============================================================
 *
 * Fetch DWRs linked to either:
 *
 * Schedule:
 *   schedule_id
 *   schedule_ids
 *
 * TodoDraft:
 *   todo_draft_id
 *   todo_draft_ids
 *
 * For bulk Schedule lookup:
 *
 * const dwrs = await getLinkedDwrs({
 *   authAxios,
 *   route: PRIVATE_ROUTE.GET_DWR_AVAILABLE_WORK,
 *   schedule_ids: ['id1', 'id2', 'id3'],
 * });
 *
 * Returns:
 *
 * [
 *   {
 *     ...
 *   }
 * ]
 */
export const getLinkedDwrs = async ({
    authAxios,
    route,
    schedule_id,
    schedule_ids,
    todo_draft_id,
    todo_draft_ids,
}) => {
    if (!authAxios) {
        console.error(
            'getLinkedDwrs: authAxios is required.'
        );

        return [];
    }

    if (!route) {
        console.error(
            'getLinkedDwrs: route is required.'
        );

        return [];
    }

    /*
     * Normalize bulk IDs.
     *
     * Supports either:
     *
     * schedule_ids: ['id1', 'id2']
     *
     * OR:
     *
     * schedule_ids: 'id1,id2'
     */
    const normalizedScheduleIds = Array.isArray(
        schedule_ids
    )
        ? schedule_ids
            .filter(Boolean)
            .map((id) => String(id))
        : schedule_ids
            ? String(schedule_ids)
                .split(',')
                .map((id) => id.trim())
                .filter(Boolean)
            : [];

    const normalizedTodoDraftIds = Array.isArray(
        todo_draft_ids
    )
        ? todo_draft_ids
            .filter(Boolean)
            .map((id) => String(id))
        : todo_draft_ids
            ? String(todo_draft_ids)
                .split(',')
                .map((id) => id.trim())
                .filter(Boolean)
            : [];

    /*
     * Parent lookup validation.
     */
    const hasScheduleId = Boolean(schedule_id);

    const hasScheduleIds =
        normalizedScheduleIds.length > 0;

    const hasTodoDraftId =
        Boolean(todo_draft_id);

    const hasTodoDraftIds =
        normalizedTodoDraftIds.length > 0;

    const hasScheduleLookup =
        hasScheduleId ||
        hasScheduleIds;

    const hasTodoDraftLookup =
        hasTodoDraftId ||
        hasTodoDraftIds;

    /*
     * Nothing supplied.
     */
    if (
        !hasScheduleLookup &&
        !hasTodoDraftLookup
    ) {
        console.error(
            'getLinkedDwrs: schedule_id(s) or todo_draft_id(s) is required.'
        );

        return [];
    }

    /*
     * Cannot mix Schedule and TodoDraft.
     */
    if (
        hasScheduleLookup &&
        hasTodoDraftLookup
    ) {
        console.error(
            'getLinkedDwrs: provide either schedule_id(s) or todo_draft_id(s), not both.'
        );

        return [];
    }

    try {
        const params = {};

        /*
         * ========================================================
         * SCHEDULE LOOKUP
         * ========================================================
         */

        if (hasScheduleLookup) {
            /*
             * Bulk lookup.
             *
             * Backend expects:
             *
             * schedule_ids=id1,id2,id3
             */
            if (normalizedScheduleIds.length > 0) {
                params.schedule_ids =
                    normalizedScheduleIds.join(',');
            }

            /*
             * Single lookup.
             */
            else if (schedule_id) {
                params.schedule_id =
                    schedule_id;
            }
        }

        /*
         * ========================================================
         * TODO DRAFT LOOKUP
         * ========================================================
         */

        if (hasTodoDraftLookup) {
            /*
             * Bulk lookup.
             */
            if (
                normalizedTodoDraftIds.length > 0
            ) {
                params.todo_draft_ids =
                    normalizedTodoDraftIds.join(',');
            }

            /*
             * Single lookup.
             */
            else if (todo_draft_id) {
                params.todo_draft_id =
                    todo_draft_id;
            }
        }

        /*
         * ========================================================
         * API REQUEST
         * ========================================================
         */

        const response =
            await authAxios.get(route, {
                params,
            });

        /*
         * Backend did not return success.
         */
        if (
            response?.data?.statusCode !== 200
        ) {
            return [];
        }

        const responseData =
            response?.data?.data || {};

        let linkedDwrs = [];

        /*
         * ========================================================
         * RESPONSE FORMAT 1
         *
         * data: {
         *   linkedDwrs: [...]
         * }
         * ========================================================
         */

        if (
            Array.isArray(
                responseData?.linkedDwrs
            )
        ) {
            linkedDwrs =
                responseData.linkedDwrs;
        }

        /*
         * ========================================================
         * RESPONSE FORMAT 2
         *
         * data: {
         *   schedules: [
         *     {
         *       _id: scheduleId,
         *       linkedDwrs: [...]
         *     }
         *   ]
         * }
         *
         * Used by the bulk Schedule lookup.
         * ========================================================
         */

        if (
            linkedDwrs.length === 0 &&
            Array.isArray(
                responseData?.schedules
            )
        ) {
            responseData.schedules.forEach(
                (schedule) => {
                    if (
                        Array.isArray(
                            schedule?.linkedDwrs
                        )
                    ) {
                        linkedDwrs.push(
                            ...schedule.linkedDwrs
                        );
                    }
                }
            );
        }

        /*
         * ========================================================
         * RESPONSE FORMAT 3
         *
         * For TodoDraft bulk lookup, support:
         *
         * data: {
         *   todoDrafts: [...]
         * }
         *
         * Each TodoDraft may contain linkedDwrs.
         * ========================================================
         */

        if (
            linkedDwrs.length === 0 &&
            Array.isArray(
                responseData?.todoDrafts
            )
        ) {
            responseData.todoDrafts.forEach(
                (todoDraft) => {
                    if (
                        Array.isArray(
                            todoDraft?.linkedDwrs
                        )
                    ) {
                        linkedDwrs.push(
                            ...todoDraft.linkedDwrs
                        );
                    }
                }
            );
        }

        /*
         * ========================================================
         * ALWAYS RETURN ARRAY
         * ========================================================
         */

        if (!Array.isArray(linkedDwrs)) {
            return [];
        }

        return linkedDwrs;
    } catch (error) {
        console.error(
            'Error fetching linked DWRs:',
            error
        );

        return [];
    }
};


/**
 * ============================================================
 * GET VISIBLE LINKED DWRs
 * ============================================================
 *
 * Filters linked DWRs to only:
 *
 * 0 = Pending
 * 1 = Approved
 *
 * Excludes:
 *
 * 2 = Rejected
 * 3 = Open
 *
 * Also sorts newest task_date first.
 */
export const getVisibleLinkedDwrs = (
    linkedDwrs = []
) => {
    if (!Array.isArray(linkedDwrs)) {
        return [];
    }

    return linkedDwrs
        .filter((dwr) => {
            const status =
                Number(dwr?.status);

            return (
                status === 0 ||
                status === 1
            );
        })
        .sort((a, b) => {
            const dateA = a?.task_date
                ? new Date(
                    a.task_date
                ).getTime()
                : 0;

            const dateB = b?.task_date
                ? new Date(
                    b.task_date
                ).getTime()
                : 0;

            return dateB - dateA;
        });
};


/**
 * ============================================================
 * FETCH VISIBLE LINKED DWRs
 * ============================================================
 *
 * Convenience function.
 *
 * Fetches the linked DWRs and immediately applies
 * the Pending/Approved filter.
 */
export const fetchVisibleLinkedDwrs = async ({
    authAxios,
    route,
    schedule_id,
    todo_draft_id,
}) => {
    const linkedDwrs =
        await getLinkedDwrs({
            authAxios,
            route,
            schedule_id,
            todo_draft_id,
        });

    return getVisibleLinkedDwrs(
        linkedDwrs
    );
};


/**
 * ============================================================
 * HAS LINKED DWR
 * ============================================================
 *
 * Returns true when the parent record has at least one:
 *
 * - Pending DWR
 * - Approved DWR
 */
export const hasLinkedDwr = (
    linkedDwrs = []
) => {
    if (!Array.isArray(linkedDwrs)) {
        return false;
    }

    return linkedDwrs.some((dwr) => {
        const status =
            Number(dwr?.status);

        return (
            status === 0 ||
            status === 1
        );
    });
};


/**
 * ============================================================
 * GET LINKED DWR COUNT
 * ============================================================
 *
 * Returns the number of Pending/Approved DWRs.
 */
export const getLinkedDwrCount = (
    linkedDwrs = []
) => {
    return getVisibleLinkedDwrs(
        linkedDwrs
    ).length;
};


/**
 * ============================================================
 * LINKED DWR MODAL
 * ============================================================
 *
 * Displays DWRs linked to a Schedule.
 *
 * Props:
 *
 * open
 *   Controls whether the modal is visible.
 *
 * onClose
 *   Closes the modal.
 *
 * schedule
 *   Selected schedule.
 *
 * dwrData
 *   DWR records to display.
 *
 * loading
 *   Loading state while fetching DWRs.
 */
export const LinkedDwrModal = ({
    open,
    onClose,
    schedule,
    dwrData = [],
    loading = false,
}) => {
    /*
     * Always make sure the modal only displays
     * Pending / Approved DWRs.
     *
     * This also protects the modal if a caller
     * accidentally passes Rejected/Open records.
     */
    const visibleDwrs =
        getVisibleLinkedDwrs(dwrData);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                },
            }}
        >
            {/* ======================================================
          MODAL HEADER
          ====================================================== */}
            <Box
                sx={{
                    background:
                        'linear-gradient(135deg, #65C8D0 0%, #3DADB5 100%)',
                    px: 2.5,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '1rem',
                        }}
                    >
                        Daily Work Reports
                    </Typography>

                    <Typography
                        sx={{
                            color:
                                'rgba(255,255,255,0.9)',
                            fontSize: '0.78rem',
                            mt: 0.25,
                        }}
                    >
                        Job{' '}
                        {schedule
                            ?.job_id
                            ?.number_str || '-'}
                        {' • '}
                        Task{' '}
                        {schedule
                            ?.task
                            ?.number_str || '-'}
                    </Typography>
                </Box>

                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        color: '#fff',
                    }}
                >
                    ✕
                </IconButton>
            </Box>


            {/* ======================================================
          MODAL BODY
          ====================================================== */}
            <Box
                sx={{
                    p: 2.5,
                }}
            >
                {/* ====================================================
            LOADING
            ==================================================== */}
                {loading ? (
                    <Box
                        sx={{
                            py: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress
                            size={28}
                            sx={{
                                color: '#3DADB5',
                            }}
                        />

                        <Typography
                            sx={{
                                mt: 1.5,
                                fontSize: '0.8rem',
                                color: '#667085',
                            }}
                        >
                            Loading DWR records...
                        </Typography>
                    </Box>

                ) : visibleDwrs.length === 0 ? (

                    /* ==================================================
                       NO RECORDS
                       ================================================== */
                    <Box
                        sx={{
                            py: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            border:
                                '1px dashed #D0D5DD',
                            borderRadius: 1.5,
                            backgroundColor:
                                '#FAFAFA',
                        }}
                    >
                        <AssignmentIcon
                            sx={{
                                fontSize: 40,
                                color: '#B0BEC5',
                                mb: 1,
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: '#475467',
                            }}
                        >
                            No DWR records found
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: '0.78rem',
                                mt: 0.5,
                                color: '#98A2B3',
                            }}
                        >
                            No pending or approved
                            DWRs are linked to this
                            schedule.
                        </Typography>
                    </Box>

                ) : (

                    /* ==================================================
                       DWR TABLE
                       ================================================== */
                    <Box
                        sx={{
                            overflowX: 'auto',
                            border:
                                '1px solid #E4E7EC',
                            borderRadius: 1.5,
                        }}
                    >
                        <table
                            style={{
                                width: '100%',
                                minWidth: '750px',
                                borderCollapse:
                                    'collapse',
                            }}
                        >
                            <thead>
                                <tr
                                    style={{
                                        backgroundColor:
                                            '#F8FAFC',
                                    }}
                                >
                                    {/* DWR */}
                                    <th
                                        style={{
                                            padding:
                                                '11px 12px',
                                            textAlign: 'left',
                                            fontSize:
                                                '0.72rem',
                                            fontWeight: 700,
                                            color: '#475467',
                                            whiteSpace:
                                                'nowrap',
                                        }}
                                    >
                                        DWR
                                    </th>

                                    {/* Employee */}
                                    <th
                                        style={{
                                            padding:
                                                '11px 12px',
                                            textAlign: 'left',
                                            fontSize:
                                                '0.72rem',
                                            fontWeight: 700,
                                            color: '#475467',
                                            whiteSpace:
                                                'nowrap',
                                        }}
                                    >
                                        Employee
                                    </th>

                                    {/* Cost Item */}
                                    <th
                                        style={{
                                            padding: '11px 12px',
                                            textAlign: 'left',
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            color: '#475467',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Cost Item
                                    </th>

                                    {/* Task Date */}
                                    <th
                                        style={{
                                            padding:
                                                '11px 12px',
                                            textAlign: 'left',
                                            fontSize:
                                                '0.72rem',
                                            fontWeight: 700,
                                            color: '#475467',
                                            whiteSpace:
                                                'nowrap',
                                        }}
                                    >
                                        Task Date
                                    </th>

                                    {/* Hours */}
                                    <th
                                        style={{
                                            padding:
                                                '11px 12px',
                                            textAlign: 'center',
                                            fontSize:
                                                '0.72rem',
                                            fontWeight: 700,
                                            color: '#475467',
                                            whiteSpace:
                                                'nowrap',
                                        }}
                                    >
                                        Hours
                                    </th>

                                    {/* Status */}
                                    <th
                                        style={{
                                            padding:
                                                '11px 12px',
                                            textAlign: 'center',
                                            fontSize:
                                                '0.72rem',
                                            fontWeight: 700,
                                            color: '#475467',
                                            whiteSpace:
                                                'nowrap',
                                        }}
                                    >
                                        Status
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {visibleDwrs.map(
                                    (
                                        dwr,
                                        index
                                    ) => {
                                        const employee =
                                            dwr?.user ||
                                            dwr?.user_data ||
                                            dwr?.userData ||
                                            dwr?.employee;

                                        const employeeName =
                                            employee?.first_name ||
                                                employee?.last_name
                                                ? `${employee?.first_name || ''} ${employee?.last_name || ''
                                                    }`.trim()
                                                : employee?.name ||
                                                dwr?.employee_name ||
                                                '-';

                                        const costItem =
                                            dwr?.billing_line_items?.labourCosts
                                                ?.map((item) => item?.costitem)
                                                .filter(Boolean)
                                                .join(', ') ||
                                            schedule?.cost_item?.costItem ||
                                            schedule?.cost_item?.costitem ||
                                            '-';                                        

                                        const status =
                                            Number(
                                                dwr?.status
                                            );

                                        const isApproved =
                                            status === 1;

                                        return (
                                            <tr
                                                key={
                                                    dwr?._id ||
                                                    index
                                                }
                                                style={{
                                                    borderTop:
                                                        '1px solid #EDF0F2',
                                                }}
                                            >
                            {/* ==================================
                            DWR
                            ================================== */}
                                                <td
                                                    style={{
                                                        padding:
                                                            '11px 12px',
                                                        fontSize:
                                                            '0.78rem',
                                                        fontWeight: 600,
                                                        color: '#344054',
                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    {dwr?.dwr_number ||
                                                        dwr?.number ||
                                                        dwr?._id ||
                                                        '-'}
                                                </td>


                                                {/* ==================================
                            EMPLOYEE
                            ================================== */}
                                                <td
                                                    style={{
                                                        padding:
                                                            '11px 12px',
                                                        fontSize:
                                                            '0.78rem',
                                                        color: '#344054',
                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    {employeeName}
                                                </td>

                                                {/* COST ITEM */}
                                                <td
                                                    style={{
                                                        padding: '11px 12px',
                                                        fontSize: '0.78rem',
                                                        color: '#344054',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {costItem}
                                                </td>


                                                {/* ==================================
                            TASK DATE
                            ================================== */}
                                                <td
                                                    style={{
                                                        padding:
                                                            '11px 12px',
                                                        fontSize:
                                                            '0.78rem',
                                                        color: '#475467',
                                                        whiteSpace:
                                                            'nowrap',
                                                    }}
                                                >
                                                    {dwr?.task_date
                                                        ? dayjs(String(dwr.task_date).substring(0, 10)).format('MM/DD/YYYY')
                                                        : dayjs().format('MM/DD/YYYY')}
                                                </td>


                                                {/* ==================================
                            HOURS
                            ================================== */}
                                                <td
                                                    style={{
                                                        padding:
                                                            '11px 12px',
                                                        textAlign:
                                                            'center',
                                                        fontSize:
                                                            '0.78rem',
                                                        fontWeight: 600,
                                                        color: '#344054',
                                                    }}
                                                >
                                                    {dwr?.approved_hours ??
                                                        dwr?.task_hour ??
                                                        dwr?.estimate_hour ??
                                                        dwr?.hours ??
                                                        0}
                                                </td>


                                                {/* ==================================
                            STATUS
                            ================================== */}
                                                <td
                                                    style={{
                                                        padding:
                                                            '11px 12px',
                                                        textAlign:
                                                            'center',
                                                    }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            isApproved
                                                                ? 'Approved'
                                                                : 'Pending'
                                                        }
                                                        sx={{
                                                            height: 24,
                                                            fontWeight: 600,
                                                            fontSize:
                                                                '0.68rem',

                                                            backgroundColor:
                                                                isApproved
                                                                    ? '#E8F5E9'
                                                                    : '#FFF4E5',

                                                            color:
                                                                isApproved
                                                                    ? '#2E7D32'
                                                                    : '#ED6C02',
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};


/**
 * ============================================================
 * PROP TYPES
 * ============================================================
 */

LinkedDwrModal.defaultProps = {
    open: false,
    onClose: () => { },
    schedule: null,
    dwrData: [],
    loading: false,
};